import { Mutation, useMutationState } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useContext } from "react";
import { useEffect } from "react";
import { ConfigContext, ReturnOrderContext } from "@/Layout";
import { CreateReturnData, returnMutationKey } from "@/api/createReturn";
import { usePrepaymentStatus } from "@/api/prepaymentStatus";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import { cn } from "@/utils/utils";

export const Route = createFileRoute("/return-instruction")({
  component: RouteComponent,
});

function RouteComponent() {
  const [createdReturn] = useMutationState({
    filters: { mutationKey: returnMutationKey },
    select: (mutation: Mutation) => mutation.state.data as CreateReturnData,
  });

  const { returnOrder } = useContext(ReturnOrderContext),
    configData = useContext(ConfigContext),
    navigate = useNavigate();

  useEffect(() => {
    if (returnOrder === undefined) {
      navigate({
        to: "/",
        search: (prev) => prev,
        replace: true,
      });
    }
  }, [returnOrder, navigate]);

  const returnRequiresPayment =
    createdReturn !== undefined && "invoice_url" in createdReturn;

  const { data: prepaymentStatus } = usePrepaymentStatus(
    returnRequiresPayment ? createdReturn?.check_id : "",
    false
  );

  useEffect(() => {
    if (!createdReturn && !(prepaymentStatus?.status === "COMPLETED"))
      navigate({
        to: "/",
        search: (prev) => prev,
        replace: true,
      });
  }, [createdReturn, prepaymentStatus, navigate]);

  useEffect(() => {
    if (returnOrder === undefined) return;
    if (!createdReturn && prepaymentStatus?.status !== "COMPLETED") return;

    const instructionUrl = `${window.location.pathname}${window.location.search}`;
    window.history.pushState(null, "", instructionUrl);

    const stayOnReturnInstruction = () => {
      navigate({
        to: "/return-instruction",
        search: (prev) => prev,
        replace: true,
      });
      window.history.pushState(null, "", instructionUrl);
    };

    window.addEventListener("popstate", stayOnReturnInstruction);

    return () => {
      window.removeEventListener("popstate", stayOnReturnInstruction);
    };
  }, [returnOrder, createdReturn, prepaymentStatus, navigate]);

  if (returnOrder === undefined) return null;

  if (!createdReturn && prepaymentStatus?.status !== "COMPLETED") return null;

  if (returnRequiresPayment && !prepaymentStatus) return null;

  const returnResult = returnRequiresPayment
    ? prepaymentStatus!
    : createdReturn;

  if ("status" in returnResult && returnResult.status == "PENDING") return null;

  let instruction;
  let labelUrl;

  if (createdReturn) {
    instruction = returnResult.return_instruction;
    labelUrl = returnResult.label_url;
  } else {
    instruction = returnResult.return_instruction;
    labelUrl = returnResult.label_url;
  }

  return (
    <ReturnContainer>
      <div className="min-w-[300px] sm:max-w-2/3">
        <h1 className="text-lg font-semibold">
          {configData?.thank_you_header}
        </h1>
        <hr className="mt-2 mb-10 sm:mb-8" />
        <div
          className={cn(
            "[&_ul]:list-disc text-sm font-light [&_p]:my-0",
            "[&_ul]:my-2 [&_ul]:pl-6 [&_strong]:font-semibold [&_p]:min-h-4"
          )}
          dangerouslySetInnerHTML={{ __html: instruction }}
        />
        {labelUrl !== null ? (
          <div className="w-full mt-8 text-sm rounded-md">
            <button
              className="bg-black rounded-none flex items-center gap-1 text-center w-1/2"
              style={{
                backgroundColor:
                  configData?.button_color && configData.button_color !== ""
                    ? configData.button_color
                    : "black",
              }}
            >
              <a
                className="w-full text-white font-light"
                target="_blank"
                href={labelUrl}
              >
                Download label
              </a>
            </button>
          </div>
        ) : null}
        <hr className="mt-12" />
        <div className="flex justify-center sm:justify-start w-full mt-1">
          <img src={configData?.logo_src} alt="logo" className="h-4" />
        </div>
      </div>
    </ReturnContainer>
  );
}
